import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Trash2, RefreshCw, ShieldCheck, ChevronLeft, ArrowDownUp } from "lucide-react";
import { formatLocalDateTime } from "../lib/constants";

const REASON_PILL = {
  "Duplicate MR": "bg-rose-100 text-rose-800 border-rose-300",
  "Wrong entry": "bg-orange-100 text-orange-800 border-orange-300",
  "Cancelled order": "bg-amber-100 text-amber-800 border-amber-300",
  "Test record": "bg-slate-100 text-slate-700 border-slate-300",
  Other: "bg-purple-100 text-purple-800 border-purple-300",
};

export default function AdminDeletions() {
  const { isPrivileged } = useAuth();
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get("/admin/deletions", { params: { limit: 200 } });
      setRows(data);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || "Failed to load deletions");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isPrivileged) {
      nav("/", { replace: true });
      return;
    }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPrivileged]);

  if (!isPrivileged) return null;

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6 pb-24 md:pb-10">
      <button
        onClick={() => nav(-1)}
        data-testid="back-btn"
        className="flex items-center gap-1 text-sm font-bold uppercase tracking-wide text-slate-600 hover:text-slate-900 mb-3"
      >
        <ChevronLeft className="h-4 w-4" /> Back
      </button>

      <div className="flex items-start justify-between flex-wrap gap-3 mb-6">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-rose-700 flex items-center gap-2">
            <ShieldCheck className="h-4 w-4" />
            Admin / Admin · Audit Trail
          </div>
          <h1 className="font-display text-3xl sm:text-4xl uppercase text-slate-900 tracking-tight">
            Recent Deletions
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            Every deleted shipment is logged here with reason, deleter, and timestamp.
          </p>
        </div>
        <button
          onClick={load}
          data-testid="deletions-refresh"
          className="rg-tap px-4 border-2 border-slate-300 hover:border-slate-900 rounded-sm flex items-center gap-2 font-bold uppercase tracking-wide text-slate-700 bg-white"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Refresh
        </button>
      </div>

      {error && (
        <div className="rg-card p-3 mb-4 border-2 border-rose-600 bg-rose-50 text-rose-800 text-sm font-bold">
          {error}
        </div>
      )}

      {loading ? (
        <div className="text-slate-500">Loading audit log…</div>
      ) : rows.length === 0 ? (
        <div className="rg-card p-10 text-center" data-testid="deletions-empty">
          <Trash2 className="h-10 w-10 mx-auto text-slate-300 mb-2" strokeWidth={2} />
          <div className="font-display text-xl text-slate-900 uppercase mb-1">
            No deletions yet
          </div>
          <div className="text-sm text-slate-600">
            Deletions made via the Danger Zone or Bulk Delete will appear here.
          </div>
        </div>
      ) : (
        <>
          {/* Desktop table */}
          <div className="hidden md:block rg-card overflow-x-auto" data-testid="deletions-table">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-slate-500 border-b-2 border-slate-300 bg-slate-50">
                  <th className="py-3 px-3 flex items-center gap-1">
                    <ArrowDownUp className="h-3 w-3" /> Deleted
                  </th>
                  <th className="py-3 px-3">MR</th>
                  <th className="py-3 px-3">Project / Station</th>
                  <th className="py-3 px-3">Reason</th>
                  <th className="py-3 px-3">Notes</th>
                  <th className="py-3 px-3">Deleted by</th>
                  <th className="py-3 px-3">Type</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr
                    key={r.id}
                    className="border-b border-slate-100 hover:bg-slate-50"
                    data-testid={`deletion-row-${r.mr_number}`}
                  >
                    <td className="py-2 px-3 text-slate-600 whitespace-nowrap">
                      {formatLocalDateTime(r.deleted_at)}
                    </td>
                    <td className="py-2 px-3 font-mono font-bold text-slate-900">
                      {r.mr_number || "—"}
                    </td>
                    <td className="py-2 px-3 text-slate-700">
                      {r.project_station || <span className="text-slate-400">—</span>}
                    </td>
                    <td className="py-2 px-3">
                      <span
                        className={`inline-block border-2 rounded-sm px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${
                          REASON_PILL[r.reason] || "bg-slate-100 border-slate-300"
                        }`}
                      >
                        {r.reason}
                      </span>
                    </td>
                    <td className="py-2 px-3 text-slate-600 max-w-[320px] truncate" title={r.notes}>
                      {r.notes || <span className="text-slate-400">—</span>}
                    </td>
                    <td className="py-2 px-3 text-slate-700">
                      <div className="font-bold leading-tight">{r.deleted_by_name}</div>
                      <div className="text-[10px] text-slate-500">{r.deleted_by_email}</div>
                    </td>
                    <td className="py-2 px-3">
                      {r.bulk ? (
                        <span className="inline-block border-2 border-rose-300 bg-rose-50 text-rose-800 rounded-sm px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider">
                          Bulk
                        </span>
                      ) : (
                        <span className="inline-block border-2 border-slate-200 bg-slate-50 text-slate-600 rounded-sm px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider">
                          Single
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <ul className="md:hidden space-y-3" data-testid="deletions-mobile-list">
            {rows.map((r) => (
              <li
                key={r.id}
                className="rg-card p-4 border-l-[6px] border-l-rose-600"
                data-testid={`deletion-card-${r.mr_number}`}
              >
                <div className="flex items-center justify-between mb-1.5">
                  <div className="font-mono text-base font-bold text-slate-900">
                    {r.mr_number}
                  </div>
                  <span
                    className={`inline-block border-2 rounded-sm px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${
                      REASON_PILL[r.reason] || "bg-slate-100 border-slate-300"
                    }`}
                  >
                    {r.reason}
                  </span>
                </div>
                {r.project_station && (
                  <div className="text-sm text-slate-700">{r.project_station}</div>
                )}
                {r.notes && (
                  <div className="text-xs text-slate-500 mt-1 italic">"{r.notes}"</div>
                )}
                <div className="flex items-center justify-between mt-2 text-xs">
                  <div className="text-slate-700">
                    by <span className="font-bold">{r.deleted_by_name}</span>
                  </div>
                  <div className="text-slate-500">{formatLocalDateTime(r.deleted_at)}</div>
                </div>
              </li>
            ))}
          </ul>
        </>
      )}

      {/* Inline link back to Roles */}
      <div className="mt-6 text-sm text-slate-600">
        Looking for user role management?{" "}
        <Link to="/admin/roles" className="font-bold text-slate-900 underline">
          Manage Roles →
        </Link>
      </div>
    </div>
  );
}
